<template>
    <div>
        <b-alert v-if="!isSubmissionExtension" variant="info" show class="mb-3">
            <i class="fas fa-info-circle"></i>
            <strong>Настройка регламентных сроков для каждого статуса заявки.</strong>
            Укажите максимальное время нахождения в статусе и настройте уведомления.
        </b-alert>

        <div v-for="status in visibleStatuses" :key="status.key" class="border rounded p-3 mb-3">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0">
                    <b-badge :variant="status.variant">{{ status.label }}</b-badge>
                </h6>
                <b-form-checkbox
                    v-if="!isSubmissionExtension"
                    v-model="localConfig[status.key].enabled"
                    @change="emitUpdate"
                    switch
                >
                    Активен
                </b-form-checkbox>
            </div>

            <div v-if="isSubmissionExtension || localConfig[status.key].enabled">
                <b-row>
                    <b-col v-if="!isSubmissionExtension" md="4">
                        <b-form-group :label="`Макс. дней в статусе`" :label-for="`${status.key}-days`">
                            <b-form-input
                                :id="`${status.key}-days`"
                                v-model.number="localConfig[status.key].max_days"
                                type="number"
                                min="0"
                                step="0.5"
                                placeholder="3"
                                @input="emitUpdate"
                            />
                            <small class="text-muted">0 = без ограничений</small>
                        </b-form-group>
                    </b-col>
                    <b-col v-if="!isSubmissionExtension" md="4">
                        <b-form-group label="Напоминание за (дней)" :label-for="`${status.key}-reminder`">
                            <b-form-input
                                :id="`${status.key}-reminder`"
                                v-model.number="localConfig[status.key].reminder_before_days"
                                type="number"
                                min="0"
                                step="0.5"
                                placeholder="1"
                                @input="emitUpdate"
                            />
                            <small class="text-muted">За сколько дней напомнить</small>
                        </b-form-group>
                    </b-col>
                    <b-col md="4" class="d-flex align-items-center">
                        <b-form-checkbox
                            v-model="localConfig[status.key].send_notifications"
                            @change="emitUpdate"
                            switch
                        >
                            Отправлять уведомления
                        </b-form-checkbox>
                    </b-col>
                </b-row>

                <b-row v-if="localConfig[status.key].send_notifications">
                    <b-col md="12">
                        <b-form-group label="Кому отправлять уведомления" :label-for="`${status.key}-notify-to`">
                            <b-form-checkbox-group
                                :id="`${status.key}-notify-to`"
                                v-model="localConfig[status.key].notify_to"
                                @change="emitUpdate"
                                stacked
                            >
                                <b-form-checkbox value="initiator">Инициатор</b-form-checkbox>
                                <b-form-checkbox value="approvers">Согласующие</b-form-checkbox>
                                <b-form-checkbox v-if="!isSubmissionExtension" value="assigners">Назначающие</b-form-checkbox>
                                <b-form-checkbox v-if="!isSubmissionExtension" value="executors">Исполнители</b-form-checkbox>
                                <b-form-checkbox value="observers">Наблюдатели</b-form-checkbox>
                            </b-form-checkbox-group>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row v-if="status.key === 'overdue'">
                    <b-col md="12">
                        <b-form-checkbox
                            v-model="localConfig[status.key].requires_explanation"
                            @change="emitUpdate"
                            switch
                        >
                            Требовать объяснение причины просрочки
                        </b-form-checkbox>
                    </b-col>
                </b-row>

                <b-alert v-if="!isSubmissionExtension" variant="light" show class="small mb-0 mt-2">
                    <i class="fas fa-comment-dots"></i>
                    <strong>Шаблон сообщения:</strong> {{ status.notificationTemplate }}
                </b-alert>
            </div>
        </div>
    </div>
</template>

<script>
import extensionProfile from '../../../../../../server/utils/Requests/timesheetExtensionProfile';
export default {
    name: "StatusConfig",
    props: {
        category: {type: String, default: null},
        value: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            statuses: [
                {
                    key: 'draft',
                    label: 'Черновик',
                    variant: 'secondary',
                    description: 'Заявка в процессе заполнения',
                    notificationTemplate: 'Не отправляется: уведомления по черновикам пропускаются.'
                },
                {
                    key: 'submitted',
                    label: 'Отправлена',
                    variant: 'info',
                    description: 'Заявка отправлена на согласование',
                    notificationTemplate: 'Требуется согласование -> заявка, тип, объект -> статус -> инициатор -> срок -> согласующие -> описание -> откройте заявку и примите решение.'
                },
                {
                    key: 'in_review',
                    label: 'На рассмотрении',
                    variant: 'warning',
                    description: 'Заявка на этапе согласования',
                    notificationTemplate: 'Требуется согласование -> заявка, тип, объект -> статус -> инициатор -> срок -> согласующие -> описание. Если ожидающих согласующих нет: Заявка на согласовании.'
                },
                {
                    key: 'returned',
                    label: 'Возвращена на доработку',
                    variant: 'warning',
                    description: 'Согласующий вернул на доработку',
                    notificationTemplate: 'Заявка возвращена -> заявка, тип, объект -> статус -> кто вернул -> причина -> исправьте и отправьте повторно.'
                },
                {
                    key: 'approved',
                    label: 'Согласована',
                    variant: 'success',
                    description: 'Все согласования получены',
                    notificationTemplate: 'Заявка согласована -> заявка, тип, объект -> статус -> инициатор -> срок.'
                },
                {
                    key: 'awaiting_executor',
                    label: 'Ожидание назначения исполнителя',
                    variant: 'info',
                    description: 'Заявка ожидает назначения исполнителя',
                    notificationTemplate: 'Если есть назначающие: Нужно назначить исполнителя -> согласование, назначающие, потенциальные исполнители. Если назначающих нет: Нужен исполнитель -> потенциальные исполнители -> возьмите заявку в работу.'
                },
                {
                    key: 'in_progress',
                    label: 'В работе',
                    variant: 'primary',
                    description: 'Исполнитель выполняет заявку',
                    notificationTemplate: 'Заявка в работе -> заявка, тип, объект -> статус -> инициатор -> срок -> активный исполнитель. При возврате из приемки: Заявка возвращена в работу.'
                },
                {
                    key: 'partially_completed',
                    label: 'Частично выполнена',
                    variant: 'warning',
                    description: 'Заявка частично выполнена',
                    notificationTemplate: 'Заявка частично выполнена -> заявка, тип, объект -> статус -> инициатор -> срок -> активный исполнитель.'
                },
                {
                    key: 'awaiting_acceptance',
                    label: 'Ожидает приемки',
                    variant: 'info',
                    description: 'Ожидает подтверждения инициатором',
                    notificationTemplate: 'Нужна приемка -> заявка, тип, объект -> статус -> текущий исполнитель -> описание -> инициатор, проверьте результат и примите заявку.'
                },
                {
                    key: 'partially_accepted',
                    label: 'Частично принята',
                    variant: 'info',
                    description: 'Заявка частично принята',
                    notificationTemplate: 'Отдельного smart-шаблона нет: используется общий fallback Статус изменён -> заявка, тип, объект -> старый статус -> новый статус -> инициатор -> срок.'
                },
                {
                    key: 'completed',
                    label: 'Выполнена',
                    variant: 'success',
                    description: 'Заявка успешно завершена',
                    notificationTemplate: 'Заявка завершена -> заявка, тип, объект -> статус -> инициатор -> причина, если она передана.'
                },
                {
                    key: 'rejected',
                    label: 'Отклонена',
                    variant: 'danger',
                    description: 'Заявка отклонена',
                    notificationTemplate: 'Заявка отклонена -> заявка, тип, объект -> статус -> инициатор -> кто отклонил -> причина.'
                },
                {
                    key: 'cancelled',
                    label: 'Отменена',
                    variant: 'dark',
                    description: 'Заявка отменена',
                    notificationTemplate: 'Заявка отменена -> заявка, тип, объект -> статус -> инициатор -> кто отменил -> причина.'
                },
                {
                    key: 'overdue',
                    label: 'Просрочена',
                    variant: 'danger',
                    description: 'Нарушены регламентные сроки',
                    notificationTemplate: 'Заявка просрочена -> заявка, тип, объект -> текущий статус -> инициатор -> срок -> активный исполнитель или потенциальные исполнители -> нужна срочная реакция.'
                }
            ],
            // Инициализируем позже в created(), чтобы statuses уже был доступен
            localConfig: {}
        }
    },
    computed: {
        isSubmissionExtension() { return extensionProfile.isExtension(this.category); },
        visibleStatuses() {
            if (!this.isSubmissionExtension) return this.statuses;
            const labels = {submitted: 'Создана', completed: 'Продление согласовано', rejected: 'Отклонена', cancelled: 'Отозвана / закрыта'};
            return this.statuses.filter(status => extensionProfile.NOTIFICATION_STATUSES.includes(status.key))
                .map(status => Object.assign({}, status, {label: labels[status.key]}));
        }
    },
    created() {
        // Инициализация конфигурации статусов после создания компонента
        this.localConfig = this.initializeConfig();
    },
    watch: {
        category() { this.localConfig = this.initializeConfig(); },
        value: {
            handler(newVal) {
                if (newVal) {
                    this.localConfig = this.mergeWithDefaults(newVal);
                }
            },
            deep: true
        }
    },
    methods: {
        initializeConfig() {
            if (this.isSubmissionExtension) return this.extensionConfig(this.value);
            const config = {};
            this.statuses.forEach(status => {
                config[status.key] = {
                    enabled: true,
                    max_days: 0,
                    reminder_before_days: 1,
                    send_notifications: true,
                    notify_to: ['initiator', 'approvers', 'assigners', 'executors'],
                    requires_explanation: status.key === 'overdue',
                    ...(this.value && this.value[status.key] ? this.value[status.key] : {})
                };
            });
            return config;
        },
        mergeWithDefaults(config) {
            if (this.isSubmissionExtension) return this.extensionConfig(config);
            const merged = {};
            this.statuses.forEach(status => {
                merged[status.key] = {
                    enabled: true,
                    max_days: 0,
                    reminder_before_days: 1,
                    send_notifications: true,
                    notify_to: ['initiator', 'approvers', 'assigners', 'executors'],
                    requires_explanation: status.key === 'overdue',
                    ...(config[status.key] || {})
                };
            });
            return merged;
        },
        extensionConfig(value) {
            const config = extensionProfile.notificationDefaults(value);
            const aliases = {approver: 'approvers', observer: 'observers', executor: 'executors', assigner: 'assigners'};
            extensionProfile.NOTIFICATION_STATUSES.forEach(key => {
                const entry = config[key];
                entry.send_notifications = entry.enabled !== false && entry.send_notifications !== false;
                entry.notify_to = (entry.notify_to || []).map(role => aliases[role] || role);
            });
            return config;
        },
        emitUpdate() {
            if (this.isSubmissionExtension) extensionProfile.NOTIFICATION_STATUSES.forEach(key => {
                this.localConfig[key].enabled = true;
            });
            this.$emit('input', JSON.parse(JSON.stringify(this.localConfig)));
        }
    }
}
</script>

<style scoped>
</style>

